import React from 'react';

/* Two-segment proportional bar with a text legend — men/women program spend,
   men/women athletic aid, and the women/men student-body split.
   Ported from `SplitBar` in MatchDetailSheet.swift.

   Identity is carried by the legend text, not by the two colors, and both
   percentages are spelled out. The segments are separated by a 2px surface gap
   so they read as two marks rather than one bar that changes color. */

/**
 * Faithful port of the Swift `short(_:)`: abbreviates money, and falls through
 * to a bare percent for the campus split, which passes percentages into the
 * same formatter. Callers therefore never mix units within one bar.
 */
function short(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `${n}%`;
}

export default function SplitBar({
  caption, leftLabel, rightLabel, left, right, leftTone = 'blue', rightTone = 'gold',
}) {
  if (left == null && right == null) return null;

  const l = left ?? 0;
  const r = right ?? 0;
  const leftFrac = (l + r) > 0 ? l / (l + r) : 0.5;
  const leftPct = Math.round(leftFrac * 100);
  const rightPct = 100 - leftPct;
  const total = `${short(left)} / ${short(right)}`;

  return (
    <div className="sd-chart sd-split">
      <div className="sd-split__head">
        <span className="sd-split__caption">{caption}</span>
        <span className="sd-split__total">{total}</span>
      </div>
      <div
        className="sd-split__bar"
        role="img"
        aria-label={`${caption}: ${leftLabel} ${leftPct} percent, ${rightLabel} ${rightPct} percent`}
      >
        <div
          className={`sd-split__seg sd-split__seg--${leftTone}`}
          style={{ flex: `0 0 ${(leftFrac * 100).toFixed(2)}%` }}
        />
        <div className={`sd-split__seg sd-split__seg--${rightTone}`} style={{ flex: '1 1 auto' }} />
      </div>
      <div className="sd-split__legend">
        <span className="sd-split__key">
          <span className={`sd-split__swatch sd-split__seg--${leftTone}`} />
          {leftLabel} {leftPct}%
        </span>
        <span className="sd-split__key">
          <span className={`sd-split__swatch sd-split__seg--${rightTone}`} />
          {rightLabel} {rightPct}%
        </span>
      </div>
    </div>
  );
}
